import { useCallback, useEffect, useRef, useState } from "react";
import type {
  Actor,
  Command,
  GuildRecord,
  JobView,
} from "@guildmaster/core/platform";
import {
  availabilityLabels,
  blankProfession,
  blankProfessionProfile,
  primaryProfessions,
  secondaryProfessions,
  professionNames,
  professionAvailability,
  professionValueSchema,
  type ProfessionDirectoryView,
  type ProfessionValue,
  type ProfessionEntry,
} from "@guildmaster/core/professions";
import { api, ApiError, client } from "./api";
import "./professions.css";

const date = (value: number) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
const waiting = (job: JobView | null) =>
  Boolean(job && ["queued", "running", "paused"].includes(job.state));
export function Professions({
  actor,
  enabled,
  jobs,
  onDenied,
  onChanged,
}: {
  actor: Actor;
  enabled: boolean;
  jobs: JobView[];
  onDenied: (error: unknown) => void;
  onChanged: () => Promise<void>;
}) {
  const [view, setView] = useState<ProfessionDirectoryView | null>(null);
  const [q, setQ] = useState(""),
    [profession, setProfession] = useState(""),
    [stage, setStage] = useState(""),
    [mainOrAlt, setMainOrAlt] = useState(""),
    [availability, setAvailability] = useState(""),
    [offset, setOffset] = useState(0);
  const [manageOwnerId, setManageOwnerId] = useState<string>();
  const [mine, setMine] = useState(
    () => new URLSearchParams(window.location.search).get("view") === "mine",
  );
  const [draft, setDraft] = useState<ProfessionValue | null>(null),
    [editing, setEditing] = useState<GuildRecord>(),
    [reason, setReason] = useState("");
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [withdrawing, setWithdrawing] = useState(false),
    [job, setJob] = useState<JobView | null>(null);
  const retryKey = useRef<{
    input: string;
    key: string;
    command: Command;
  } | null>(null);
  const editorHeading = useRef<HTMLHeadingElement>(null);
  const requestSerial = useRef(0);
  const officer = actor.officeAccess && actor.capabilities.includes("approve");
  const refresh = useCallback(async () => {
    const serial = ++requestSerial.current;
    const query = new URLSearchParams({
      q,
      profession,
      stage,
      mainOrAlt,
      availability,
      offset: String(offset),
      ...(manageOwnerId ? { manageOwnerId } : {}),
    });
    const next = await api<ProfessionDirectoryView>(
      "/api/professions?" + query,
    );
    if (serial === requestSerial.current) setView(next);
  }, [q, profession, stage, mainOrAlt, availability, offset, manageOwnerId]);
  const failure = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && [401, 403].includes(error.status)) {
        setView(null);
        setDraft(null);
        onDenied(error);
      }
      setError(
        error instanceof Error
          ? error.message
          : "The directory could not be refreshed",
      );
    },
    [onDenied],
  );
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const read = () => {
      if (active && !document.hidden)
        void refresh().catch((error) => {
          if (active) failure(error);
        });
    };
    const timer = setTimeout(read, 200);
    const interval = setInterval(read, 30_000);
    window.addEventListener("focus", read);
    return () => {
      active = false;
      clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener("focus", read);
    };
  }, [enabled, refresh, failure]);
  useEffect(() => {
    const latest = jobs
      .filter(
        (item) =>
          item.command.type === "record.put" &&
          item.command.value.kind === "professions",
      )
      .sort((a, b) => b.createdAt - a.createdAt)[0];
    if (latest)
      setJob((current) =>
        !current || latest.updatedAt >= current.updatedAt ? latest : current,
      );
  }, [jobs]);
  useEffect(() => {
    if (!job || !waiting(job)) return;
    let active = true;
    const timer = setInterval(() => {
      void client
        .jobs()
        .then(async (values) => {
          const next = values.find((value) => value.id === job.id);
          if (!active || !next) return;
          setJob(next);
          if (!waiting(next)) {
            await refresh();
            await onChanged();
          }
        })
        .catch((error) => {
          if (active) failure(error);
        });
    }, 3000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [job?.id, job?.state, refresh, onChanged, failure]);
  useEffect(() => {
    if (draft) editorHeading.current?.focus();
  }, [Boolean(draft)]);
  useEffect(() => {
    setDraft(null);
    setEditing(undefined);
    setReason("");
    setWithdrawing(false);
  }, [manageOwnerId]);
  function edit(record?: GuildRecord) {
    setEditing(record);
    setDraft(
      record?.value.kind === "professions"
        ? structuredClone(record.value)
        : blankProfessionProfile(),
    );
    setReason("");
    setError("");
    setWithdrawing(false);
    setMine(true);
    retryKey.current = null;
  }
  async function save(withdraw = false) {
    if (!draft || !view) return;
    setError("");
    try {
      const parsed = professionValueSchema.safeParse({
        ...draft,
        effectiveAt: Date.now(),
        ...(withdraw ? { status: "withdrawn", entries: [] } : {}),
      });
      if (!parsed.success)
        throw Error(
          parsed.error.issues.map((issue) => issue.message).join(". "),
        );
      const value = parsed.data;
      if (view.editorOwnerId !== actor.userId && reason.trim().length < 3)
        throw Error("Give a reason for this officer correction");
      const id = editing?.id ?? "profession-" + crypto.randomUUID();
      // Preserve the exact command and key across an uncertain HTTP retry.
      const command: Command = {
        type: "record.put",
        contextId: "forever-behind",
        id,
        ownerId: view.editorOwnerId,
        expectedRevision: editing?.revision ?? 0,
        value,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      };
      const fingerprint = JSON.stringify({
        ...command,
        id: editing?.id ?? "new",
        value: { ...value, effectiveAt: 0 },
      });
      let payload: Command = command;
      const previous = retryKey.current;
      if (previous?.input === fingerprint && previous.command)
        payload = previous.command;
      else
        retryKey.current = {
          input: fingerprint,
          key: crypto.randomUUID(),
          command,
        };
      setBusy(true);
      const accepted = await client.submit(payload, retryKey.current!.key);
      setJob(accepted);
      setDraft(null);
      setEditing(undefined);
      setWithdrawing(false);
      retryKey.current = null;
      await onChanged();
    } catch (error) {
      failure(error);
    } finally {
      setBusy(false);
    }
  }
  function toggle(name: ProfessionEntry["profession"], checked: boolean) {
    if (!draft) return;
    setDraft({
      ...draft,
      entries: checked
        ? [...draft.entries, blankProfession(name)]
        : draft.entries.filter((entry) => entry.profession !== name),
    });
  }
  function patchEntry(
    name: ProfessionEntry["profession"],
    patch: Partial<ProfessionEntry>,
  ) {
    if (draft)
      setDraft({
        ...draft,
        entries: draft.entries.map((entry) =>
          entry.profession === name ? { ...entry, ...patch } : entry,
        ),
      });
  }
  if (!enabled)
    return (
      <div className="empty">
        <h2>Professions are not open yet</h2>
        <p>The directory is being prepared for guild members.</p>
      </div>
    );
  return (
    <div className="profession-page">
      <div className="profession-intro">
        <div>
          <p className="eyebrow">GUILD CRAFTING</p>
          <h2>Find a guildmate. Make something.</h2>
          <p>
            Shared plans and self-reported skills. Ask your crafter about the
            exact recipe and materials.
          </p>
        </div>
        <button
          className="button primary"
          onClick={() => {
            setMine(!mine);
            setManageOwnerId(undefined);
          }}
        >
          {mine ? "Browse directory" : "My professions"}
        </button>
      </div>
      {error && (
        <p className="notice error" role="alert">
          {error}{" "}
          <button
            className="text-button"
            onClick={() =>
              void refresh()
                .then(() => setError(""))
                .catch(failure)
            }
          >
            Reload directory
          </button>
        </p>
      )}
      {job && (
        <div
          className={
            "notice " +
            (["failed", "needs-review"].includes(job.state) ? "error" : "")
          }
          role="status"
        >
          <strong>
            {job.state === "complete"
              ? "Saved"
              : job.state === "queued"
                ? "Queued, waiting for the runner"
                : job.state === "running"
                  ? "Saving your professions"
                  : job.state === "failed"
                    ? "Update failed"
                    : job.state === "paused"
                      ? "Update paused"
                      : job.state === "cancelled"
                        ? "Update cancelled"
                        : "Update needs review"}
          </strong>
          <p>
            {job.message}
            {job.result &&
            typeof job.result === "object" &&
            "error" in job.result
              ? " | " + String(job.result.error)
              : ""}
          </p>
          {waiting(job) && (
            <p>
              Your directory entry changes only after the update is committed
              and synchronized.
            </p>
          )}
        </div>
      )}
      {view?.stale && (
        <p className="notice" role="status">
          Some membership checks are out of date. Affected entries are hidden
          until Discord synchronization recovers.
        </p>
      )}
      {mine ? (
        <section className="profession-mine" aria-label="Manage professions">
          <div className="profession-toolbar">
            <div>
              <h2>
                {manageOwnerId ? "Manage member professions" : "My professions"}
              </h2>
              <p>
                Saved entries are visible to current guild members. Plans do not
                reserve a profession.
              </p>
            </div>
            <button
              className="button"
              disabled={busy || waiting(job) || !view}
              onClick={() => edit()}
            >
              Add character or plan
            </button>
            {manageOwnerId && (
              <button
                className="text-button"
                onClick={() => setManageOwnerId(undefined)}
              >
                Back to my entries
              </button>
            )}
          </div>
          {!draft &&
            view?.editable.map((record) => {
              const value = record.value;
              if (value.kind !== "professions") return null;
              return (
                <article className="profession-card" key={record.id}>
                  <div>
                    <span className="badge">{value.stage}</span>
                    <h3>
                      {value.stage === "planned"
                        ? value.plannedLabel
                        : (view.characters.find(
                            (character) => character.id === value.characterId,
                          )?.name ?? "Character no longer active")}
                    </h3>
                    <p>
                      {value.entries
                        .map((entry) => entry.profession)
                        .join(" · ")}
                    </p>
                    <p className="fine">
                      Updated {date(record.updatedAt)}
                      {record.changeReason
                        ? " | Correction: " + record.changeReason
                        : ""}
                    </p>
                  </div>
                  <button
                    className="button subtle"
                    disabled={busy || waiting(job)}
                    onClick={() => edit(record)}
                  >
                    Edit
                  </button>
                </article>
              );
            })}
          {!draft && view && !view.editable.length && (
            <div className="empty">
              <h3>No professions listed yet</h3>
              <p>
                Add a planned main or alt now. Actual professions can be linked
                to your verified characters when they are available.
              </p>
            </div>
          )}
          {draft && (
            <form
              className="profession-editor"
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <h3 ref={editorHeading} tabIndex={-1}>
                {editing
                  ? "Edit profession profile"
                  : "Add a character or plan"}
              </h3>
              <div className="profession-fields">
                <label>
                  Stage
                  <select
                    value={draft.stage}
                    disabled={
                      editing?.value.kind === "professions" &&
                      editing.value.stage === "actual"
                    }
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        stage: event.target.value as ProfessionValue["stage"],
                        characterId: null,
                        plannedLabel:
                          event.target.value === "actual" ? "" : "Planned main",
                        entries: draft.entries.map((entry) => ({
                          ...entry,
                          skill: null,
                          availability: "not-specified",
                        })),
                      })
                    }
                  >
                    <option value="planned">Planned professions</option>
                    <option value="actual" disabled={!view?.characters.length}>
                      Actual professions
                    </option>
                  </select>
                </label>
                {draft.stage === "planned" ? (
                  <>
                    <label>
                      Planned character or slot
                      <input
                        required
                        maxLength={60}
                        value={draft.plannedLabel}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            plannedLabel: event.target.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      Main or alt
                      <select
                        value={draft.mainOrAlt}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            mainOrAlt: event.target.value as "main" | "alt",
                          })
                        }
                      >
                        <option value="main">Main</option>
                        <option value="alt">Alt</option>
                      </select>
                    </label>
                  </>
                ) : (
                  <label>
                    Verified character
                    <select
                      required
                      value={draft.characterId ?? ""}
                      disabled={
                        editing?.value.kind === "professions" &&
                        editing.value.stage === "actual"
                      }
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          characterId: event.target.value || null,
                        })
                      }
                    >
                      <option value="">Select your character</option>
                      {view?.characters.map((character) => (
                        <option key={character.id} value={character.id}>
                          {character.name} | {character.mainOrAlt}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <p className="fine">
                {draft.stage === "planned"
                  ? "Plans have no current skill or availability. Conversion requires a verified character and fresh details."
                  : "Character ownership is verified separately. Profession skills and services are self-reported."}
              </p>
              {[
                {
                  title: "Primary professions | choose up to two",
                  names: primaryProfessions,
                },
                { title: "Secondary professions", names: secondaryProfessions },
              ].map((group) => (
                <fieldset key={group.title}>
                  <legend>{group.title}</legend>
                  <div className="profession-choices">
                    {group.names.map((name) => (
                      <label key={name}>
                        <input
                          type="checkbox"
                          checked={draft.entries.some(
                            (entry) => entry.profession === name,
                          )}
                          onChange={(event) =>
                            toggle(name, event.target.checked)
                          }
                        />
                        {name}
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
              {draft.entries.map((entry) => (
                <fieldset key={entry.profession}>
                  <legend>{entry.profession}</legend>
                  <div className="profession-fields">
                    <label>
                      Current skill | optional
                      <input
                        type="number"
                        min={1}
                        max={1000}
                        step={1}
                        disabled={draft.stage === "planned"}
                        value={entry.skill ?? ""}
                        onChange={(event) =>
                          patchEntry(entry.profession, {
                            skill:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                          })
                        }
                      />
                    </label>
                    <label>
                      Specialization | optional
                      <input
                        maxLength={100}
                        value={entry.specialization}
                        onChange={(event) =>
                          patchEntry(entry.profession, {
                            specialization: event.target.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      Availability
                      <select
                        disabled={draft.stage === "planned"}
                        value={entry.availability}
                        onChange={(event) =>
                          patchEntry(entry.profession, {
                            availability: event.target
                              .value as ProfessionEntry["availability"],
                          })
                        }
                      >
                        {professionAvailability.map((value) => (
                          <option key={value} value={value}>
                            {availabilityLabels[value]}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label>
                    Service notes | optional
                    <textarea
                      maxLength={600}
                      rows={2}
                      placeholder="Known crafts, materials to bring, or plans"
                      value={entry.notes}
                      onChange={(event) =>
                        patchEntry(entry.profession, {
                          notes: event.target.value,
                        })
                      }
                    />
                  </label>
                </fieldset>
              ))}
              {view && view.editorOwnerId !== actor.userId && (
                <label>
                  Reason for officer correction
                  <textarea
                    required
                    minLength={3}
                    maxLength={500}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </label>
              )}
              <div className="profession-actions">
                <button
                  className="button primary"
                  disabled={busy || waiting(job)}
                >
                  {busy ? "Submitting…" : "Save to guild directory"}
                </button>
                <button
                  type="button"
                  className="button subtle"
                  disabled={busy}
                  onClick={() => setDraft(null)}
                >
                  Cancel
                </button>
                {editing && (
                  <button
                    type="button"
                    className="text-button"
                    disabled={busy || waiting(job)}
                    onClick={() => setWithdrawing(true)}
                  >
                    Remove entry
                  </button>
                )}
              </div>
              {withdrawing && (
                <div className="notice" role="alert">
                  <p>
                    Remove this profile from the guild directory? Its revision
                    history is retained.
                  </p>
                  <button
                    type="button"
                    className="button"
                    disabled={busy}
                    onClick={() => void save(true)}
                  >
                    Yes, remove entry
                  </button>
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => setWithdrawing(false)}
                  >
                    Keep entry
                  </button>
                </div>
              )}
            </form>
          )}
        </section>
      ) : (
        <>
          <div
            className="profession-coverage"
            aria-label="Guild coverage by profession"
          >
            {view?.coverage.map((row) => (
              <button
                key={row.profession}
                className={profession === row.profession ? "selected" : ""}
                aria-pressed={profession === row.profession}
                onClick={() => {
                  setProfession(
                    profession === row.profession ? "" : row.profession,
                  );
                  setOffset(0);
                }}
              >
                <strong>{row.profession}</strong>
                <span>
                  <b>{row.actual}</b> actual · <b>{row.planned}</b> planned
                </span>
                <small>{row.available} available to help</small>
              </button>
            ))}
          </div>
          <p className="fine">
            Coverage counts distinct members per profession. Plans and actual
            skills are counted separately.
          </p>
          <div className="profession-filters">
            <label>
              Search
              <input
                type="search"
                placeholder="Member, character, craft or specialty"
                value={q}
                onChange={(event) => {
                  setQ(event.target.value);
                  setOffset(0);
                }}
              />
            </label>
            {[
              {
                label: "Profession",
                value: profession,
                options: professionNames,
                setter: setProfession,
              },
              {
                label: "Stage",
                value: stage,
                options: ["planned", "actual"],
                setter: setStage,
              },
              {
                label: "Character",
                value: mainOrAlt,
                options: ["main", "alt"],
                setter: setMainOrAlt,
              },
              {
                label: "Availability",
                value: availability,
                options: professionAvailability,
                setter: setAvailability,
              },
            ].map((filter) => (
              <label key={filter.label}>
                {filter.label}
                <select
                  value={filter.value}
                  onChange={(event) => {
                    filter.setter(event.target.value);
                    setOffset(0);
                  }}
                >
                  <option value="">All</option>
                  {filter.options.map((value) => (
                    <option key={value} value={value}>
                      {filter.label === "Availability"
                        ? availabilityLabels[
                            value as ProfessionEntry["availability"]
                          ]
                        : value}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <div className="profession-results" aria-live="polite">
            <p className="fine">
              {view
                ? `${view.total} character ${view.total === 1 ? "profile" : "profiles"}`
                : "Loading professions…"}
            </p>
            {view?.profiles.map((card) => (
              <article key={card.id} className="profession-card">
                <header>
                  <div>
                    <span
                      className={
                        "badge " + (card.stage === "actual" ? "green" : "")
                      }
                    >
                      {card.stage === "planned"
                        ? "Planned"
                        : "Actual | self-reported"}
                    </span>
                    <h3>{card.characterName}</h3>
                    <p>
                      {card.displayName} · {card.mainOrAlt}
                    </p>
                  </div>
                  <a
                    className="button subtle"
                    href={"https://discord.com/users/" + card.ownerId}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Contact in Discord
                  </a>
                </header>
                <div className="profession-services">
                  {card.entries.map((entry) => (
                    <div key={entry.profession}>
                      <strong>{entry.profession}</strong>
                      <span>
                        {card.stage === "planned"
                          ? "Planned"
                          : entry.skill === null
                            ? "Skill not supplied"
                            : "Skill " + entry.skill}
                        {entry.specialization
                          ? " · " + entry.specialization
                          : ""}
                      </span>
                      <span
                        className={
                          entry.availability === "available"
                            ? "profession-available"
                            : "fine"
                        }
                      >
                        {card.stage === "planned"
                          ? "Availability not established"
                          : availabilityLabels[entry.availability]}
                      </span>
                      {entry.notes && <p className="preserve">{entry.notes}</p>}
                    </div>
                  ))}
                </div>
                <footer>
                  <span className="fine">Updated {date(card.updatedAt)}</span>
                  {(card.ownerId === actor.userId || officer) && (
                    <button
                      className="text-button"
                      onClick={() => {
                        setManageOwnerId(
                          card.ownerId === actor.userId
                            ? undefined
                            : card.ownerId,
                        );
                        setMine(true);
                      }}
                    >
                      Manage entries
                    </button>
                  )}
                </footer>
              </article>
            ))}
            {view && !view.total && (
              <div className="empty">
                <h3>No matching professions listed</h3>
                <p>
                  {q || profession || stage || mainOrAlt || availability
                    ? "Try another filter, or ask in #professions."
                    : "Be the first to share a profession plan. Missing entries mean unknown guild coverage."}
                </p>
              </div>
            )}
          </div>
          {view && view.total > 20 && (
            <nav className="profession-actions" aria-label="Directory pages">
              <button
                className="button subtle"
                disabled={!offset}
                onClick={() => setOffset(Math.max(0, offset - 20))}
              >
                Previous
              </button>
              <span>
                {offset + 1} to {Math.min(offset + 20, view.total)} of{" "}
                {view.total}
              </span>
              <button
                className="button subtle"
                disabled={offset + 20 >= view.total}
                onClick={() => setOffset(offset + 20)}
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
